import EditIcon from '@mui/icons-material/Edit';
import Person from '@mui/icons-material/Person';
import WorkspacePremium from '@mui/icons-material/WorkspacePremium';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Pagination from '@mui/material/Pagination';
import { UserService } from '@pingum/api-services';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Add,
  LocationOn,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import BarnIcon from '../../../../../components/CustomIcons/Barn';
import { setTimezones } from '../../../../../redux/app/actions';
import { getHorseLocations } from '../../../../../redux/horse/actions';
import { grey } from '../../../../../utils/brandColors';
import { getLocationTypeDisplayName } from '../../../../../utils/enumHelper';
import {
  isUserAdmin,
  isUserOwner,
} from '../../../../../utils/tenantAuthenticationHelper';
import AddEditModal from './components/AddEditModal';
import LocationsCardLoading from './components/LocationsCardLoading';

export const getIcon = (location) => {
  if (location.type === 'Competition') {
    return <WorkspacePremium sx={{ color: grey[500] }} />
  } if (location.type === 'Trainer') {
    return <Person sx={{ color: grey[500] }} />
  }
  return <BarnIcon color={grey[500]} fontSize={20} margin="4" />
}

const LocationsCardComponent = ({ id }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    horseLocations,
    isFetchingHorseLocations,
  } = useSelector((state) => state.horse);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const { user } = useSelector((state) => state.user);

  // State
  const [currentHorseLocations, setCurrentLocations] = React.useState(null);
  const [upcomingLocations, setUpcomingLocations] = React.useState([]);
  const [pastLocations, setPastLocations] = React.useState([]);
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseLocation, setSelectedHorseLocation] = React.useState(null);
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'startDate',
      sort: 'desc',
    },
  ]);

  const roles = UserService.getUserRolesForTenant(
    user,
    selectedBarn.tenant,
  );
  // eslint-disable-next-line no-unused-vars
  const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    dispatch(getHorseLocations(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  React.useEffect(() => {
    // find current horse location
    if (horseLocations.content.length > 0) {
      const now = new Date();
      const sublist = horseLocations.content.filter((l) => new Date(l.startDate) < now
        && (l.endDate === null || new Date(l.endDate) > now));
      setCurrentLocations(sublist.length > 0
        ? sublist
        : null);

      setUpcomingLocations(horseLocations.content.filter((l) => new Date(l.startDate).getTime() > now.getTime()));
      setPastLocations(horseLocations.content.filter((l) => l.endDate && new Date(l.endDate).getTime() < now.getTime()));

      if (sublist.length > 0) {
        dispatch(setTimezones(sublist[0].location.timezone))
      }
    } else {
      setCurrentLocations(null);
    }
  }, [horseLocations]);

  const handleEditClick = (horseLocation) => {
    setSelectedHorseLocation(horseLocation);
    setOpenAddEditModal(true);
  };

  const handleAddClick = () => {
    setSelectedHorseLocation(null);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    // reload
    dispatch(getHorseLocations(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  };

  return (
    <>
      {isFetchingHorseLocations
        ? <LocationsCardLoading />
        : (
          <Paper
            sx={{
              borderRadius: 3,
              p: 2,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar><LocationOn /></Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Locations
                </Typography>
              </Stack>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddClick}>
                Add Location
              </Button>
            </Stack>
            <Box>
              <List
                aria-label="horse current location"
                subheader={(
                  <ListSubheader component="div" id="nested-list-subheader-upcoming">
                    Current Location
                  </ListSubheader>
                )}
              >
                {
                  currentHorseLocations
                    ? currentHorseLocations.map(((horseLocation) => (
                      <ListItem
                        key={horseLocation.id}
                        secondaryAction={currentHorseLocations && (
                          <IconButton edge="end" aria-label="edit" onClick={() => { handleEditClick(horseLocation); }}>
                            <EditIcon />
                          </IconButton>
                        )}
                        disablePadding
                        onClick={() => { handleEditClick(horseLocation); }}
                      >
                        <ListItemButton selected>
                          <ListItemIcon>{getIcon(horseLocation.location)}</ListItemIcon>
                          <ListItemText
                            primary={horseLocation.location.name}
                            secondary={(
                              <React.Fragment>
                                <Typography
                                  sx={{ display: 'inline' }}
                                  component="span"
                                  variant="body2"
                                >
                                  <b>{getLocationTypeDisplayName(horseLocation.location)}</b>
                                </Typography>
                                {` - ${Moment(horseLocation.startDate).format('ll')} ${horseLocation.endDate
                                  ? (`through ${Moment(horseLocation.endDate).format('ll')}`)
                                  : 'through no end date specified'}`}
                              </React.Fragment>
                            )}
                            primaryTypographyProps={{ variant: 'h5' }}
                            secondaryTypographyProps={{ sx: { textWrap: 'auto' } }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )))
                    : (
                      <ListItemButton disabled><ListItemText secondary="No current location" /></ListItemButton>
                    )
                }
              </List>
              {
                upcomingLocations.length > 0 && (
                  <>
                    <Divider />
                    <List
                      aria-label="horse upcoming locations"
                      subheader={(
                        <ListSubheader component="div" id="nested-list-subheader-upcoming">
                          Upcoming Locations
                        </ListSubheader>
                      )}
                    >
                      {
                        upcomingLocations.map((hl) => (
                          <ListItem
                            key={hl.id}
                            secondaryAction={(
                              <IconButton edge="end" aria-label="edit" onClick={() => { handleEditClick(hl); }}>
                                <EditIcon />
                              </IconButton>
                            )}
                            disablePadding
                            onClick={() => { handleEditClick(hl); }}
                          >
                            <ListItemButton>
                              <ListItemIcon>{getIcon(hl.location)}</ListItemIcon>
                              <ListItemText
                                primary={`${hl.location.name} (UPCOMING)`}
                                secondary={(
                                  <React.Fragment>
                                    <Typography
                                      sx={{ display: 'inline' }}
                                      component="span"
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      <b>{getLocationTypeDisplayName(hl.location)}</b>
                                    </Typography>
                                    {` - ${Moment(hl.startDate).format('ll')} ${hl.endDate
                                      ? (`through ${Moment(hl.endDate).format('ll')}`)
                                      : ''}`}
                                  </React.Fragment>
                                )}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      }
                    </List>
                  </>
                )
              }
              <Divider />
              <List
                aria-label="horse past locations"
                subheader={(
                  <ListSubheader component="div" id="nested-list-subheader">
                    Past Locations
                  </ListSubheader>
                )}
              >
                {
                  pastLocations.length === 0
                    ? (
                      <ListItemButton disabled><ListItemText secondary="No past locations" /></ListItemButton>
                    )
                    : pastLocations.map((hl) => (
                      <ListItem
                        key={hl.id}
                        secondaryAction={(
                          <IconButton edge="end" aria-label="edit" onClick={() => { handleEditClick(hl); }}>
                            <EditIcon />
                          </IconButton>
                        )}
                        disablePadding
                        onClick={() => { handleEditClick(hl); }}
                      >
                        <ListItemButton dense>
                          <ListItemIcon>{getIcon(hl.location)}</ListItemIcon>
                          <ListItemText
                            primary={hl.location.name}
                            secondary={(
                              <React.Fragment>
                                <Typography
                                  sx={{ display: 'inline' }}
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                >
                                  <b>{getLocationTypeDisplayName(hl.location)}</b>
                                </Typography>
                                {` - ${Moment(hl.startDate).format('ll')} through ${Moment(hl.endDate).format('ll')}`}
                              </React.Fragment>
                            )}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                }
              </List>
              {(horseLocations.totalPages || 0) > 1 && (
                <Pagination count={horseLocations.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseLocations} />
              )}
            </Box>
          </Paper>
        )}
      <AddEditModal
        open={openAddEditModal}
        horseLocation={selectedHorseLocation}
        currentHorseLocation={currentHorseLocations
          ? currentHorseLocations[0]
          : null}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
    </>
  );
};

LocationsCardComponent.propTypes = { id: PropTypes.string.isRequired };

LocationsCardComponent.defaultProps = {};

export default LocationsCardComponent;
